import { Link } from "react-router-dom";
import { Phone, Envelope, MapPin, FacebookLogo, InstagramLogo } from "@phosphor-icons/react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { href: "/about", label: "About Us" },
    { href: "/contact", label: "Contact" },
    { href: "/faq", label: "FAQ" },
    { href: "/blog", label: "Blog" },
  ];

  const serviceAreas = [
    { href: "/service-areas/fargo-nd", label: "Fargo, ND" },
    { href: "/service-areas/moorhead-mn", label: "Moorhead, MN" },
    { href: "/service-areas/west-fargo-nd", label: "West Fargo, ND" },
    { href: "/service-areas/detroit-lakes-mn", label: "Detroit Lakes, MN" },
    { href: "/service-areas/fergus-falls-mn", label: "Fergus Falls, MN" },
  ];

  return (
    <footer className="bg-[#1C1914] text-[#F7F5F0]">
      <div className="px-6 md:px-12 lg:px-24 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#C5A059] rounded-lg flex items-center justify-center">
                <span className="text-[#1C1914] font-bold text-xl font-serif">L</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold font-serif">Legacy Liquidators</h3>
                <p className="text-xs text-[#F7F5F0]/70">LLC</p>
              </div>
            </div>
            <p className="text-[#F7F5F0]/80 text-sm leading-relaxed mb-6">
              Compassionate estate sale services for families across Fargo-Moorhead and surrounding communities. No need to clean first—we handle everything.
            </p>
            <div className="flex gap-4">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-[#F7F5F0]/10 hover:bg-[#C5A059] rounded-md transition-colors"
                aria-label="Facebook"
                data-testid="footer-facebook"
              >
                <FacebookLogo weight="fill" className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-[#F7F5F0]/10 hover:bg-[#C5A059] rounded-md transition-colors"
                aria-label="Instagram"
                data-testid="footer-instagram"
              >
                <InstagramLogo weight="fill" className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider mb-6">Quick Links</h4>
            <nav className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-[#F7F5F0]/80 hover:text-[#C5A059] transition-colors text-sm"
                  data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider mb-6">Service Areas</h4>
            <nav className="flex flex-col gap-3">
              {serviceAreas.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-[#F7F5F0]/80 hover:text-[#C5A059] transition-colors text-sm"
                  data-testid={`footer-area-${link.label.toLowerCase().replace(/[,\s]/g, '-')}`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/service-areas"
                className="text-[#C5A059] hover:text-[#F7F5F0] transition-colors text-sm font-medium"
                data-testid="footer-all-areas"
              >
                View All Areas →
              </Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[#C5A059] font-semibold text-sm uppercase tracking-wider mb-6">Contact Us</h4>
            <div className="space-y-4">
              <a
                href="tel:2182982420"
                className="flex items-center gap-3 text-[#F7F5F0] hover:text-[#C5A059] transition-colors"
                data-testid="footer-phone"
              >
                <Phone weight="fill" className="w-5 h-5 text-[#C5A059]" />
                <span className="text-sm">218-298-2420</span>
              </a>
              <a
                href="mailto:kathy@FargoEstateSales.com"
                className="flex items-center gap-3 text-[#F7F5F0]/80 hover:text-[#C5A059] transition-colors"
                data-testid="footer-email"
              >
                <Envelope weight="fill" className="w-5 h-5 text-[#C5A059]" />
                <span className="text-sm">kathy@FargoEstateSales.com</span>
              </a>
              <div className="flex items-start gap-3 text-[#F7F5F0]/80">
                <MapPin weight="fill" className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                <span className="text-sm">Serving Fargo, ND and surrounding communities within 60 miles</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#F7F5F0]/10">
        <div className="px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#F7F5F0]/60 text-sm">
              © {currentYear} Legacy Liquidators LLC. All rights reserved.
            </p>
            <p className="text-[#F7F5F0]/60 text-sm">
              Estate Sales in Fargo, ND · Moorhead, MN · West Fargo, ND
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
